import { useFormField } from '@calendly/form';
import { I18n } from '@calendly/i18n';
import { ContextualBanner } from '@calendly/ui/components/contextual-banner';
import { IconMailShare } from '@calendly/ui/components/glyphs/IconMailShare';
import { IconMessages } from '@calendly/ui/components/glyphs/IconMessages';
import { AlertWarningCircleIcon } from '@calendly/ui/components/icons';
import { InlineBanner } from '@calendly/ui/components/inline-banner';
import { BodyText, TitleText } from '@calendly/ui/components/typography';
import { useSelector } from 'react-redux';
import { isOnFreeStageSelector } from 'features/billing';
import { useEventType } from 'features/event_type_provider';
import { useIsSmsDeactivated } from 'features/organization_settings';
import { useIsNotificationCustomizationRestricted } from 'hooks/useIsNotificationCustomizationRestricted';
import { isAnyLocksEnabled } from 'services/event_type_locking';
import { CrudActivationSwitch } from './crud_activation_switch';
import { crudSectionTitles, getCrudConfirmationCardSubtitle, getCrudTextReminderSubtitle, getCrudTimingsSubtitle } from './crud_notification_card_copy';
import { NotificationCard } from './notification_card';
import { useSelectedSectionsActions } from '../../../components/selected_sections';
import { useCalendarSettingsUrl } from '../../hooks';
import { getEffectiveNotificationType } from '../../notifications/calendar_connection_utils';
import { buildSectionHandler, getNotificationTypeCards, sectionTitles } from '../../notifications/utils';
const sectionCss = "s2shg6x";
const headerCss = "h1ymtzg8";
const cardsCss = "c1s4thtp";

// Reactive per-field binding so each switch re-renders when its enabled flag
// changes. Reading nested values off useFormContext().values does not re-subscribe
// the component, which left the toggles visually unresponsive.
const useNotificationEnabledField = kind => {
  const [{
    value
  },, {
    setValue
  }] = useFormField({
    name: `notificationEnabled.${kind}`
  });
  return [value, setValue];
};

// New CRUD notifications "Basic notifications" section. Mirrors the data and
// behavior of the legacy BasicNotificationsSection (activation state, timing
// subtitles, edit drawers, billing-gated toggles) but renders the redesigned
// card layout. Persistence still flows through the existing shared logic.
export const CrudBasicNotificationsSection = () => {
  const eventType = useEventType();
  const reconnectCalendarUrl = useCalendarSettingsUrl();
  const {
    selectSection
  } = useSelectedSectionsActions();
  const isSmsDeactivated = useIsSmsDeactivated();
  const isOnFreeStage = useSelector(isOnFreeStageSelector);
  const isNotificationCustomizationRestricted = useIsNotificationCustomizationRestricted();
  const [emailReminderEnabled, setEmailReminderEnabled] = useNotificationEnabledField('invitee_reminder');
  const [textReminderEnabled, setTextReminderEnabled] = useNotificationEnabledField('invitee_sms_reminder');
  const [emailFollowUpEnabled, setEmailFollowUpEnabled] = useNotificationEnabledField('invitee_email_follow_up');
  const [{
    value: notificationType = eventType.notification_type
  }] = useFormField({
    name: 'notificationType'
  });
  const emailReminderActivated = !!emailReminderEnabled && !isOnFreeStage;
  const textReminderActivated = !!textReminderEnabled && !isOnFreeStage;
  const emailFollowUpActivated = !!emailFollowUpEnabled && !isOnFreeStage;
  const etLocked = !eventType.isMETParent && isAnyLocksEnabled(eventType, ['v3_notifications']);
  const effectiveNotificationType = getEffectiveNotificationType(eventType, notificationType);
  const showCalendarDisconnectedBanner = effectiveNotificationType !== notificationType;
  const notificationTypeCards = getNotificationTypeCards(effectiveNotificationType, selectSection, etLocked);
  const emailRemindersTitle = crudSectionTitles.emailReminder();
  const textRemindersTitle = crudSectionTitles.textReminder();
  const emailFollowUpTitle = crudSectionTitles.emailFollowUp();
  const emailReminderDrawerTitle = sectionTitles.emailReminders();
  const textReminderDrawerTitle = sectionTitles.textReminders();
  const emailFollowUpDrawerTitle = sectionTitles.emailFollowUp();
  const emailReminderHandler = buildSectionHandler('email_reminders', emailReminderDrawerTitle, selectSection);
  const textReminderHandler = buildSectionHandler('text_reminders', textReminderDrawerTitle, selectSection);
  const emailFollowUpHandler = buildSectionHandler('email_follow_up', emailFollowUpDrawerTitle, selectSection);
  return <div className={sectionCss} data-testid="crud-basic-notifications-section">
      <div className={headerCss}>
        <TitleText level={3} size="medium" as="h3">
          {I18n.t('publisher.event_type_editor.sections.communications.basic_notifications')}
        </TitleText>
        <BodyText color="muted">
          {I18n.t('publisher.event_type_editor.sections.more_options.notifications.basic_notifications_helper')}
        </BodyText>
      </div>

      {isNotificationCustomizationRestricted && <ContextualBanner variant="warning" icon={AlertWarningCircleIcon}>
          {I18n.t('publisher.event_types.notifications.notification_customization_restricted')}
        </ContextualBanner>}

      {showCalendarDisconnectedBanner && <div data-testid="calendar-disconnected-banner">
          <InlineBanner variant="warning" isDismissible={false} title={I18n.t('publisher.event_type_editor.sections.more_options.notifications.calendar_disconnected.title')} message={I18n.t('publisher.event_type_editor.sections.more_options.notifications.calendar_disconnected.message')} primaryButton={{
        decoration: 'primary',
        label: I18n.t('publisher.event_type_editor.sections.more_options.notifications.calendar_disconnected.reconnect_calendar'),
        onClick: () => {
          window.location.assign(reconnectCalendarUrl);
        }
      }} />
        </div>}

      <div className={cardsCss}>
        {notificationTypeCards.map(card => <NotificationCard key={card.htmlId} data-testid={`notification-card-${card.htmlId}`} icon={card.iconGlyph} iconActive title={card.title} subtitle={getCrudConfirmationCardSubtitle(card.htmlId)} onEdit={card.readOnly ? undefined : card.sectionHandler} />)}

        <NotificationCard data-testid="notification-card-email-reminders" icon={IconMailShare} iconActive={emailReminderActivated} title={emailRemindersTitle} titleMuted={!emailReminderActivated} subtitle={getCrudTimingsSubtitle({
        eventType,
        notificationName: 'invitee_reminder'
      })} onEdit={etLocked ? undefined : emailReminderHandler} disabled={etLocked} toggle={etLocked ? null : <CrudActivationSwitch kind="invitee_reminder" checked={emailReminderActivated} onToggle={() => setEmailReminderEnabled(!emailReminderEnabled)} aria-label={emailRemindersTitle} />} />

        <NotificationCard data-testid="notification-card-text-reminders" icon={IconMessages} iconActive={textReminderActivated} title={textRemindersTitle} titleMuted={!textReminderActivated} subtitle={getCrudTextReminderSubtitle(isSmsDeactivated, eventType)} onEdit={etLocked || isSmsDeactivated ? undefined : textReminderHandler} disabled={etLocked || !!isSmsDeactivated} toggle={etLocked || isSmsDeactivated ? null : <CrudActivationSwitch kind="invitee_sms_reminder" checked={textReminderActivated} onToggle={() => setTextReminderEnabled(!textReminderEnabled)} aria-label={textRemindersTitle} />} />

        <NotificationCard data-testid="notification-card-email-follow-up" icon={IconMailShare} iconActive={emailFollowUpActivated} title={emailFollowUpTitle} titleMuted={!emailFollowUpActivated} subtitle={getCrudTimingsSubtitle({
        eventType,
        notificationName: 'invitee_email_follow_up'
      })} onEdit={etLocked ? undefined : emailFollowUpHandler} disabled={etLocked} toggle={etLocked ? null : <CrudActivationSwitch kind="invitee_email_follow_up" checked={emailFollowUpActivated} onToggle={() => setEmailFollowUpEnabled(!emailFollowUpEnabled)} aria-label={emailFollowUpTitle} />} />
      </div>
    </div>;
};

require("./crud_basic_notifications_section.wyw-in-js.css!=!../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./crud_basic_notifications_section.js");